import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AgentMiddlewareResponse,
  createMiddlewareEntry,
  deleteMiddlewareEntry,
  getAgentMiddleware,
  type MiddlewareEntry,
  replaceMiddlewareRaw,
  updateMiddlewareEntry,
} from "@/api/admin-api"

import { GC_TIME, MEDIUM_STALE_TIME } from "./_shared"

export function useAgentMiddlewareQuery(agentName: string | null, enabled = true) {
  return useQuery<AgentMiddlewareResponse>({
    queryKey: ["admin", "middleware", "agent", agentName],
    queryFn: () => getAgentMiddleware(agentName as string),
    enabled: enabled && !!agentName,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

function invalidateMiddleware(queryClient: ReturnType<typeof useQueryClient>, agentName: string) {
  void queryClient.invalidateQueries({ queryKey: ["admin", "middleware", "agent", agentName] })
}

export function useCreateMiddlewareMutation(agentName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entry: Omit<MiddlewareEntry, "id"> & { id?: string }) =>
      createMiddlewareEntry(agentName, entry),
    onSuccess: () => invalidateMiddleware(queryClient, agentName),
  })
}

export function useUpdateMiddlewareMutation(agentName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      middlewareId,
      entry,
      expectedHash,
      reason,
      source,
    }: {
      middlewareId: string
      entry: Partial<MiddlewareEntry> & { type: string }
      expectedHash?: string
      reason?: string
      source?: string
    }) =>
      updateMiddlewareEntry({
        agentName,
        middlewareId,
        entry,
        expectedHash,
        reason,
        source,
      }),
    onSuccess: () => invalidateMiddleware(queryClient, agentName),
  })
}

export function useDeleteMiddlewareMutation(agentName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (middlewareId: string) => deleteMiddlewareEntry(agentName, middlewareId),
    onSuccess: () => invalidateMiddleware(queryClient, agentName),
  })
}

export function useReplaceMiddlewareRawMutation(agentName: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      middleware,
      expectedHash,
      reason,
    }: {
      middleware: MiddlewareEntry[]
      expectedHash: string
      reason: string
    }) => replaceMiddlewareRaw(agentName, middleware, { expectedHash, reason }),
    onSuccess: () => invalidateMiddleware(queryClient, agentName),
  })
}
